import { BehaviorSubject, Observable, catchError, map, of } from "rxjs";
import { Injectable } from "@angular/core";
import { HttpClient, HttpHeaders, HttpParams, HttpResponse } from "@angular/common/http";
import { AppParams } from "../models/appParams";
import {
  ImportJobsDTO,
  Job,
  JobAction,
  JobDirectoryPathsI,
} from "../models/job";
import { EnvironmentService } from "./environment.service";
import { HeaderService } from "./header.service";
import { CloudTypes } from "../models/globals";
import { MatSnackBar } from "@angular/material/snack-bar";

@Injectable({
  providedIn: "root",
})
export class JobsService {
  depName; // for storing deployment name
  activeJob: Job | null = null;
  dashboardFilterApplied: string | null = null;

  constructor(
    private http: HttpClient,
    private env: EnvironmentService,
    private _headerService: HeaderService,
    private snackBar: MatSnackBar
  ) { }

  // observable subjects containing data
  uploadValues: any[] = [
    null,
    undefined,
    100,
    "Failed",
    "100",
    "100%",
    " : Upload Completed",
    "Upload Completed",
    " : Upload Completed",
    " : Uploaded 100%",
    " : File Already Exists.",
  ];

  private jobName = new BehaviorSubject<string>("");
  public jobData = this.jobName.asObservable(); //contains jobName which is active/ clicked

  sendJobData(name: string) {
    this.jobName.next(name);
  }

  private syncstoreUsed = new BehaviorSubject<string>("");
  public syncstoreUsed$ = this.syncstoreUsed.asObservable(); //contains syncstoreUsed which is active/ clicked

  sendSyncstoreUsed(name: string) {
    this.syncstoreUsed.next(name);
  }

  private activeTab = new BehaviorSubject<number>(1);
  public ActiveTab = this.activeTab.asObservable(); //contains index of tab which is active (job details/ syncstore/ workstore ) of Jobs

  sendActiveTab(value: number) {
    this.activeTab.next(value);
  }

  private jobNode = new BehaviorSubject<string>("");
  public JobNode = this.jobNode.asObservable(); //contains node name of Job which is active/ clicked

  sendJobNode(name: string) {
    this.jobNode.next(name);
  }

  private scrollUp = new BehaviorSubject<boolean>(false);
  public ScrollUp = this.scrollUp.asObservable(); // used when user click on clear logs in Jobs> Storage then the scroll will go to top.

  sendScrollUpData(value: boolean) {
    this.scrollUp.next(value);
  }

  private maximumCores = new BehaviorSubject<number>(null);
  public MaximumCores = this.maximumCores.asObservable();

  updateMaximumCores(value: number) {
    if (value != this.maximumCores.value) {
      this.maximumCores.next(value);
    }
  }

  private deleteJobObservable = new BehaviorSubject<boolean>(false);
  public deleteJobObservable$ = this.deleteJobObservable.asObservable();

  // Method to trigger job deletion
  triggerJobDeletion() {
    this.deleteJobObservable.next(true);
  }

  // save selected job's work & sync directory paths
  private jobDirectoryPaths = new BehaviorSubject<JobDirectoryPathsI>(null);
  public JobDirectoryPaths = this.jobDirectoryPaths.asObservable();

  updateJobDirectoryPaths(newPath: JobDirectoryPathsI): void {
    let arePathsSame: boolean =
      JSON.stringify(newPath) === JSON.stringify(this.jobDirectoryPaths?.value);
    if (!arePathsSame) {
      this.jobDirectoryPaths.next(newPath);
    }
  }

  get getJobDirectoryPaths(): JobDirectoryPathsI {
    if (this.jobDirectoryPaths) {
      return this.jobDirectoryPaths?.value;
    }
  }

  // perform job action from advance details panel
  private SendJobAction = new BehaviorSubject<{
    jobName: string;
    appName: string;
    action: JobAction;
  }>(null);
  public sendJobAction = this.SendJobAction.asObservable();

  updateJobAction(action: {
    jobName: string;
    appName: string;
    action: JobAction;
  }): void {
    this.SendJobAction.next(action);
  }

  // api calls
  checkJobOutputDirectory(
    defaultOutputDirectory: string,
    outputDirectory: string,
    userOutDirectory: string
  ): string | null {
    if (!userOutDirectory || userOutDirectory == "") {
      if (!outputDirectory || outputDirectory == "") {
        return defaultOutputDirectory;
      }
      if (defaultOutputDirectory == outputDirectory) {
        return null;
      }
      if (defaultOutputDirectory?.split("/") == outputDirectory?.split("/")) {
        return null;
      }
      if (defaultOutputDirectory != outputDirectory) {
        if (!["\\", "/"].includes(outputDirectory.slice(-1)))
          outputDirectory += outputDirectory.includes("\\") ? "\\" : "/";
        return outputDirectory;
      }
      if (outputDirectory == "" || !outputDirectory) {
        return defaultOutputDirectory;
      }
    } else {
      if (!userOutDirectory || userOutDirectory == "") {
        return defaultOutputDirectory;
      }
      if (defaultOutputDirectory == userOutDirectory) {
        return null;
      }
      if (defaultOutputDirectory?.split("/") == userOutDirectory?.split("/")) {
        return null;
      }
      if (defaultOutputDirectory != userOutDirectory) {
        if (["\\", "/"].includes(userOutDirectory.slice(-1)))
          userOutDirectory += userOutDirectory.includes("\\") ? "\\" : "/";
        return userOutDirectory;
      }
      if (userOutDirectory == "" || !userOutDirectory) {
        return defaultOutputDirectory;
      }
    }
  }

  triggerAnActionForJob(
    depName: string,
    jobName: string,
    actionText: string,
    actionValue: number,
    flag?: string,
    priority?: string,
    command?: string,
    customAction?: string,
    partition?: string
  ) {
    try {
      let multipleJobs: boolean = jobName.split(",").length > 1;
      if (jobName == "") {
        jobName = "runcommandnotspecifictojob";
      }

      let baseUrl =
        this.env.envVariables.httpProtocol +
        this.env.envVariables.webHost +
        "JobInfoes/Deployment/" +
        depName +
        "/jobs/" +
        (multipleJobs ? "Selected" : jobName) +
        "/TriggerJobAction";

      let headers = new HttpHeaders();
      headers = headers
        .set("Content-Type", "application/json")
        .set("Cache-Control", "no-cache")
        .set("Deployment", depName)
        .set("MultipleJobs", multipleJobs ? jobName : "")
        .set("CustomAction", customAction ? customAction : "")
        .set("Priority", priority)
        .set("Partition", partition ? partition : "");
      if (actionText.includes("AutoShut")) {
        headers = headers.append("AutoShutFlag", flag);
      }
      if (actionText.includes("Stop Allocated")) {
        headers = headers.append("StopAllocatedFlag", flag);
      }

      let options = { headers: headers };
      var jsonData = {
        "actionName": actionText,
        "actionValue": actionValue,
        "actionCommand": command ? command : "",
      };

      return this.http.post<any>(baseUrl, JSON.stringify(jsonData), options);
    } catch (e) {
      this.snackBar.open("Error performing action for job", "X", {
        duration: 3000,
      });
    }
  }

  getApplicationTags(depName: string, appName: string) {
    let baseUrl =
      this.env.envVariables.httpProtocol +
      this.env.envVariables.webHost +
      "JobInfoes/Deployment/" +
      depName +
      "/ApplicationJobTags";
    let headers = new HttpHeaders();
    headers = headers
      .set("Content-Type", "application/json; charset=utf-8")
      .set("Cache-Control", "no-cache")
      .set("Deployment", depName)
      .set("Application", appName);
    return this.http.get(baseUrl, { headers: headers });
  }

  getAllJobsforDeployment(depName: string) {
    let baseUrl =
      this.env.envVariables.httpProtocol +
      this.env.envVariables.webHost +
      "JobInfoes/Deployment/" +
      depName +
      "/alljobs";
    let headers = new HttpHeaders();
    headers = headers
      .set("Content-Type", "application/json; charset=utf-8")
      .set("Cache-Control", "no-cache")
      .set("Deployment", depName);
    return this.http.get(baseUrl, { headers: headers });
  }

  getAllJobsForUser(depName: string) {
    let baseUrl =
      this.env.envVariables.httpProtocol +
      this.env.envVariables.webHost +
      "JobInfoes/Deployment/" +
      depName +
      "/userjobs";
    let headers = new HttpHeaders();
    headers = headers
      .set("Content-Type", "application/json; charset=utf-8")
      .set("Cache-Control", "no-cache")
      .set("Deployment", depName);
    return this.http.get(baseUrl, { headers: headers });
  }

  getJobDetailsForUser(
    depName: string,
    jobName: string,
    jobTemplatename: string = "",
    appName: string = ""
  ) {
    if (jobName === null || jobName === "") {
      jobName = "NA";
    }
    let baseUrl =
      this.env.envVariables.httpProtocol +
      this.env.envVariables.webHost +
      "JobInfoes/Deployment/" +
      depName +
      "/jobs/Name=" +
      jobName;

    let headers = new HttpHeaders();
    headers = headers
      .set("Content-Type", "application/json; charset=utf-8")
      .set("Cache-Control", "no-cache")
      .set("JobTemplateName", jobTemplatename)
      .set("Application", appName)
      .set("Deployment", depName);
    return this.http.get(baseUrl, { headers: headers });
  }

  getNodeWorkstore(depName: string, jobName: string, nodeName: string) {
    let baseUrl =
      this.env.envVariables.httpProtocol +
      this.env.envVariables.webHost +
      "JobInfoes/Deployment/" +
      depName +
      "/jobs/Name=" +
      jobName;
    let headers = new HttpHeaders();
    headers = headers
      .set("Content-Type", "application/json; charset=utf-8")
      .set("Cache-Control", "no-cache")
      .set("Deployment", depName);
    return this.http.get(baseUrl, { headers: headers });
  }

  submitJob(depName: string, appParams: AppParams) {
    try {
      if (
        this._headerService.getCloudNameForDeployment(depName) ==
        CloudTypes.Oracle
      ) {
        appParams.JobGenericProperties.FilesToCopy =
          appParams.JobGenericProperties.FilesToCopy.replace(
            this.env.envVariables.syncStoreRoute + "/",
            ""
          )
            ?.split("/")
            ?.map((p) => p?.split("@")[0])
            ?.join("/");
      }
      let baseUrl =
        this.env.envVariables.httpProtocol +
        this.env.envVariables.webHost +
        "JobInfoes/Deployment/" +
        depName +
        "/jobs/Submitjob";

      let headers = new HttpHeaders();
      headers = headers
        .set("Content-Type", "application/json")
        .set("Cache-Control", "no-cache")
        .set("Deployment", depName);

      let options = { headers: headers };

      return this.http.post<any>(baseUrl, JSON.stringify(appParams), options);
    } catch (e) { }
  }

  viewJobOutput(depName: string, jobName: string) {
    try {
      let baseUrl =
        this.env.envVariables.httpProtocol +
        this.env.envVariables.webHost +
        "JobInfoes/Deployment/" +
        depName +
        "/jobs/viewoutput/JobName=" +
        jobName;
      let headers = new HttpHeaders();
      headers = headers
        .set("Content-Type", "application/json; charset=utf-8")
        .set("Cache-Control", "no-cache")
        .set("Deployment", depName);
      return this.http.get(baseUrl, { headers: headers });
    } catch (e) { }
  }

  GetJobOutputLocation(depName: string, jobName: string) {
    try {
      let baseUrl =
        this.env.envVariables.httpProtocol +
        this.env.envVariables.webHost +
        "JobInfoes/Deployment/" +
        depName +
        "/jobs/OutputPath/JobName=" +
        jobName;
      let headers = new HttpHeaders();
      headers = headers
        .set("Content-Type", "application/json; charset=utf-8")
        .set("Cache-Control", "no-cache")
        .set("Deployment", depName);
      return this.http.get(baseUrl, { headers: headers });
    } catch (e) { }
  }

  DownloadJobData(
    depName: string,
    notShowFields
  ): Observable<HttpResponse<any>> {
    let baseUrl =
      this.env.envVariables.httpProtocol +
      this.env.envVariables.webHost +
      "JobInfoes/Deployment/" +
      depName +
      "/jobs/DownloadJobData";
    let headers = new HttpHeaders();
    headers = headers
      .set("Content-Type", "application/json; charset=utf-8")
      .set("Cache-Control", "no-cache")
      .set("Deployment", depName)
      .set("NotShowFields", notShowFields);
    return this.http.post(baseUrl, notShowFields, {
      headers: headers,
      observe: "response",
      responseType: "blob",
    });
  }

  getJobDownloadInfo(depName: string, period: string) {
    let baseUrl =
      this.env.envVariables.httpProtocol +
      this.env.envVariables.webHost +
      "JobInfoes/Deployment/" +
      depName +
      "/jobs/GetJobDownloadData/Period=" +
      period;
    let headers = new HttpHeaders();
    headers = headers
      .set("Content-Type", "text/plain; charset=utf-8")
      .set("Cache-Control", "no-cache")
      .set("Deployment", depName);
    return this.http.get(baseUrl, { headers: headers });
  }

  //getJobPerUser(depName: string, jobUserName: string, entries: number, getAllDepUser?: boolean) {
  //  let baseUrl = this.env.envVariables.httpProtocol + this.env.envVariables.webHost + 'JobInfoes/Deployment/' + depName + '/GetAllDepUser/' + getAllDepUser + '/jobs/UserId=' + jobUserName;
  //  let headers = new HttpHeaders();
  //  headers = headers.set('Content-Type', 'application/json; charset=utf-8')
  //    .set('Cache-Control', 'no-cache')
  //    .set('Deployment', depName)
  //    .set('Entries', entries.toString());
  //  return this.http.get(baseUrl, { headers: headers });
  //}

  getJobPerUser(
    depName: string,
    jobUserName: string,
    entries: number,
    getAllDepUser?: boolean,
    isQueryActive?: boolean,
    queryString?: string
  ) {
    let baseUrl =
      this.env.envVariables.httpProtocol +
      this.env.envVariables.webHost +
      "JobInfoes/Deployment/" +
      depName +
      "/GetAllDepUser/" +
      getAllDepUser +
      "/jobs/UserId=" +
      jobUserName;
    let headers = new HttpHeaders();
    headers = headers
      .set("Content-Type", "application/json; charset=utf-8")
      .set("Cache-Control", "no-cache")
      .set("Deployment", depName)
      .set("Entries", entries.toString())
      .set("IsQueryActive", isQueryActive?.toString())
      .set("Query", JSON.stringify(queryString));
    return this.http.get(baseUrl, { headers: headers });
  }

  getJobByQuery(depName: string, Query: string) {
    let baseUrl =
      this.env.envVariables.httpProtocol +
      this.env.envVariables.webHost +
      "JobInfoes/Deployment/" +
      depName +
      "/GetJobsForQuery";
    let headers = new HttpHeaders();
    headers = headers
      .set("Content-Type", "application/json; charset=utf-8")
      .set("Cache-Control", "no-cache")
      .set("Deployment", depName)
      .set("Query", JSON.stringify(Query));
    return this.http.get(baseUrl, { headers: headers });
  }

  getJobPerPage(depName: string, jobUserName: string, pageNumber: string) {
    let baseUrl =
      this.env.envVariables.httpProtocol +
      this.env.envVariables.webHost +
      "JobInfoes/Deployment/" +
      depName +
      "/jobs/UserId=" +
      jobUserName +
      "/PageNumber=" +
      pageNumber;
    let headers = new HttpHeaders();
    headers = headers
      .set("Content-Type", "application/json; charset=utf-8")
      .set("Cache-Control", "no-cache")
      .set("Deployment", depName)
      .set("PageNumber", pageNumber);
    return this.http.get(baseUrl, { headers: headers });
  }

  deleteJob(
    depName: string,
    jobName: string,
    deleteFromBackend: string,
    DeleteWorkstore: string,
    DeleteSyncstore: string
  ) {
    let baseUrl =
      this.env.envVariables.httpProtocol +
      this.env.envVariables.webHost +
      "JobInfoes/Deployment/" +
      depName +
      "/jobs/deletejob/JobName=" +
      jobName;

    let headers = new HttpHeaders();
    headers = headers
      .set("Content-Type", "application/json")
      .set("Cache-Control", "no-cache")
      .set("DeleteFromBackend", deleteFromBackend)
      .set("DeleteWorkstore", DeleteWorkstore)
      .set("DeleteSyncstore", DeleteSyncstore)
      .set("Deployment", depName);

    return this.http.put<any>(baseUrl, null, { headers: headers });
  }

  deleteMultipleJobs(
    depName: string,
    jobNames: string,
    deleteFromBackend: string,
    DeleteWorkstore: string,
    DeleteSyncstore: string
  ) {
    let baseUrl =
      this.env.envVariables.httpProtocol +
      this.env.envVariables.webHost +
      "JobInfoes/Deployment/" +
      depName +
      "/jobs/deletemultiplejob";

    let headers = new HttpHeaders();
    headers = headers
      .set("Content-Type", "application/json")
      .set("Cache-Control", "no-cache")
      .set("DeleteFromBackend", deleteFromBackend)
      .set("DeleteWorkstore", DeleteWorkstore)
      .set("DeleteSyncstore", DeleteSyncstore)
      .set("Deployment", depName);

    return this.http.put<any>(baseUrl, jobNames.split(","), {
      headers: headers,
    });
  }

  getExtraParams(depName: string, appName: string, requestedCores: string) {
    let baseUrl = `${this.env.envVariables.httpProtocol}${this.env.envVariables.webHost}JobInfoes/ExtraParam/Deployment/${depName}/AppName/${appName}/Cores/${requestedCores}`;
    let headers = new HttpHeaders();
    headers = headers
      .set("Content-Type", "application/json; charset=utf-8")
      .set("Cache-Control", "no-cache")
      .set("Deployment", depName);
    return this.http.get(baseUrl, { headers: headers });
  }

  getJobCount(depName: string, role: string, getAllDepUser?: boolean) {
    let baseUrl =
      this.env.envVariables.httpProtocol +
      this.env.envVariables.webHost +
      "JobInfoes/Deployment/" +
      depName +
      "/GetAllDepUser/" +
      getAllDepUser +
      "/JobCount";
    let headers = new HttpHeaders();
    headers = headers
      .set("Content-Type", "application/json; charset=utf-8")
      .set("Cache-Control", "no-cache")
      .set("Deployment", depName)
      .set("Role", role);
    return this.http.get(baseUrl, { headers: headers });
  }

  getProfileIdForDeploymentId(depName: string) {
    let baseUrl =
      this.env.envVariables.httpProtocol +
      this.env.envVariables.webHost +
      "JobInfoes/Deployment/" +
      depName +
      "/ProfileId";
    let headers = new HttpHeaders();
    headers = headers
      .set("Content-Type", "application/json; charset=utf-8")
      .set("Cache-Control", "no-cache");
    return this.http.get(baseUrl, { headers: headers });
  }

  updateJobTags(depName: string, jobName: string, jobTag: string) {
    const baseUrl = `${this.env.envVariables.httpProtocol}${this.env.envVariables.webHost}JobInfoes/Deployment/${depName}/jobs/updateTag/JobName=${jobName}/JobTag/`;
    const headers = new HttpHeaders()
      .set("Content-Type", "application/json; charset=utf-8")
      .set("Cache-Control", "no-cache");

    const params = new HttpParams().set('jobTag', jobTag || '');

    return this.http.put(baseUrl, null, { headers, params });
  }

  updateCustomTags(depName: string, jobName: string, customTag: string) {
    const baseUrl = `${this.env.envVariables.httpProtocol}${this.env.envVariables.webHost}JobInfoes/Deployment/${depName}/jobs/updateTag/JobName=${jobName}/CustomTag/`;
    const headers = new HttpHeaders()
      .set("Content-Type", "application/json; charset=utf-8")
      .set("Cache-Control", "no-cache");

    const params = new HttpParams().set('customTag', customTag || '');

    return this.http.put(baseUrl, null, { headers, params });
  }

  viewGraphOutput(depName: string, jobName: string) {
    let baseUrl =
      this.env.envVariables.httpProtocol +
      this.env.envVariables.webHost +
      "JobInfoes/Deployment/" +
      depName +
      "/jobs/GraphData/JobName=" +
      jobName;
    let headers = new HttpHeaders();
    headers = headers
      .set("Content-Type", "application/json; charset=utf-8")
      .set("Cache-Control", "no-cache");
    return this.http.get(baseUrl, { headers: headers });
  }

  hasActiveJobs(depName: string) {
    let baseUrl =
      this.env.envVariables.httpProtocol +
      this.env.envVariables.webHost +
      "JobInfoes/Deployment/" +
      depName +
      "/ActiveJobs";
    let headers = new HttpHeaders();
    headers = headers
      .set("Content-Type", "application/json; charset=utf-8")
      .set("Cache-Control", "no-cache")
      .set("Deployment", depName);
    return this.http.get(baseUrl, { headers: headers });
  }

  getJobTagsOfAllJobs(depName: string) {
    let baseUrl =
      this.env.envVariables.httpProtocol +
      this.env.envVariables.webHost +
      "JobInfoes/Deployment/" +
      depName +
      "/JobTags";
    let headers = new HttpHeaders();
    headers = headers
      .set("Content-Type", "application/json; charset=utf-8")
      .set("Cache-Control", "no-cache");
    return this.http.get(baseUrl, { headers: headers });
  }

  getQueuedJobSequenceForUser(depName: string) {
    let baseUrl =
      this.env.envVariables.httpProtocol +
      this.env.envVariables.webHost +
      "JobInfoes/Deployment/" +
      depName +
      "/GetQueuedJobSequence";
    return this.http.get(baseUrl);
  }

  setQueuedJobSequenceForUser(depName: string, jobIds: string) {
    let baseUrl =
      this.env.envVariables.httpProtocol +
      this.env.envVariables.webHost +
      "JobInfoes/Deployment/" +
      depName +
      "/SetQueuedJobSequence";
    let headers = new HttpHeaders();
    headers = headers
      .set("Content-Type", "application/json; charset=utf-8")
      .set("Cache-Control", "no-cache")
      .set("JobIds", jobIds);
    return this.http.post(baseUrl, null, { headers });
  }

  getJobCountService(
    depName: string,
    role: string,
    jobUserId: string,
    getAllDepUser?: boolean
  ): Observable<any> {
    let baseUrl =
      this.env.envVariables.httpProtocol +
      this.env.envVariables.webHost +
      "JobInfoes/Deployment/" +
      depName +
      "/JobUser/" +
      jobUserId +
      "/JobCount";
    let headers = new HttpHeaders();
    headers = headers
      .set("Content-Type", "application/json; charset=utf-8")
      .set("Cache-Control", "no-cache")
      .set("Deployment", depName)
      .set("Role", role);
    return this.http.get(baseUrl, { headers: headers });
  }

  checkUserLicenseAvailability(
    depName: string,
    application: string,
    appVersion: string,
    cores: number
  ): Observable<boolean> {
    let baseUrl =
      this.env.envVariables.httpProtocol +
      this.env.envVariables.webHost +
      "JobInfoes/Deployment/" +
      depName +
      "/CheckUserLicenseAvailability";
    let headers = new HttpHeaders();
    headers = headers
      .set("Content-Type", "application/json; charset=utf-8")
      .set("Cache-Control", "no-cache")
      .set("Application", application)
      .set("AppVersion", appVersion)
      .set("Cores", cores.toString());
    return this.http.get(baseUrl, { headers: headers }).pipe(
      map((response: any) => {
        if (response?.isLicenseAvailable) return response?.isLicenseAvailable;
        else return false;
      }),
      catchError((error: any) => {
        console.error(error);
        return of(false); // Return an Observable that emits `false`
      })
    );
  }

  importJobs(jobData: ImportJobsDTO[], depName: string) {
    let baseUrl =
      this.env.envVariables.httpProtocol +
      this.env.envVariables.webHost +
      "JobInfoes/ImportJobs";
    let headers = new HttpHeaders();
    headers = headers
      .set("Content-Type", "application/json; charset=utf-8")
      .set("Cache-Control", "no-cache")
      .set("DeploymentName", depName);

    return this.http.post(baseUrl, jobData, { headers: headers });
  }

  getActiveJobSummary(depName: string): Observable<any> {
    let baseUrl =
      this.env.envVariables.httpProtocol +
      this.env.envVariables.webHost +
      "JobInfoes/Deployment/" +
      depName +
      "/activeJobSummary";
    let headers = new HttpHeaders();
    headers = headers
      .set("Content-Type", "application/json; charset=utf-8")
      .set("Cache-Control", "no-cache");
    return this.http.get(baseUrl, { headers: headers });
  }

  downloadRepoFromGithub(
    depName: string,
    userName: string,
    repoName: string,
    repoUrl: string
  ) {
    let baseUrl =
      this.env.envVariables.httpProtocol +
      this.env.envVariables.webHost +
      "JobInfoes/Deployment/" +
      depName +
      "/DownloadRepo";
    let headers = new HttpHeaders();
    headers = headers
      .set("Content-Type", "application/json; charset=utf-8")
      .set("Cache-Control", "no-cache");
    let repoData = {
      repoUrl: repoUrl,
      userName: userName,
      repoName: repoName,
    };
    return this.http.post(baseUrl, repoData, { headers: headers });
  }

  getNodeListOfJob(depName: string, nodeGroup: string, userId: string) {
    let baseUrl =
      this.env.envVariables.httpProtocol +
      this.env.envVariables.webHost +
      "Deployment/" +
      depName +
      "/NodeGroup=" +
      nodeGroup +
      "/NodeList";
    let headers = new HttpHeaders();
    headers = headers
      .set("Content-Type", "application/json; charset=utf-8")
      .set("Cache-Control", "no-cache")
      .set("UserId", userId)
    return this.http.get(baseUrl, { headers: headers });
  }
}
